import { SparklesIcon, XMarkIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import ReactCardFlip from 'react-card-flip';
import RGL, { Layout, WidthProvider } from 'react-grid-layout';
import { useTranslation } from 'react-i18next';

import LoadingOverlay from '@/common/components/LoadingOverlay';
import { useBoardStore } from '@/common/stores/useBoardStore';
import { useWidgetStore } from '@/common/stores/useWidgetStore';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import cn from '@/common/utils/cn';

const ReactGridLayout = WidthProvider(RGL);

interface GridItem {
  id: string;
  widget_id?: string;
  component: React.ComponentType<{ widgetId: string; metadata?: Record<string, unknown> }>;
  configComponent?: React.ComponentType<{
    widgetId: string;
    onClose: () => void;
    metadata?: Record<string, unknown>;
  }>;
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  metadata?: Record<string, unknown>;
  adjustable?: boolean;
  isLocked?: boolean;
}

interface GridProps {
  items: GridItem[];
  onRemoveWidget: (id: string) => void;
  onLockedWidgetClick?: () => void;
}

export default function Grid({ items, onRemoveWidget, onLockedWidgetClick }: GridProps) {
  const { t } = useTranslation();
  const currentBoard = useBoardStore((state) => state.currentBoard);
  const { layouts, fetchWidgets, saveLayout, loading } = useWidgetStore();
  const [flippedWidgets, setFlippedWidgets] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (currentBoard) {
      fetchWidgets();
    }
  }, [currentBoard, fetchWidgets]);

  const handleLayoutChange = (newLayout: Layout[]) => {
    saveLayout(newLayout);
  };

  const toggleConfig = (widgetId: string) => {
    setFlippedWidgets((prev) => {
      const newFlipped = new Set(prev);
      if (newFlipped.has(widgetId)) {
        newFlipped.delete(widgetId);
      } else {
        newFlipped.add(widgetId);
      }
      return newFlipped;
    });
  };

  const handleConfigClose = (widgetId: string) => {
    setFlippedWidgets((prev) => {
      const newFlipped = new Set(prev);
      newFlipped.delete(widgetId);
      return newFlipped;
    });
  };

  if (loading) {
    return (
      <div>
        <LoadingOverlay />
      </div>
    );
  }

  return (
    <ReactGridLayout
      className="layout"
      cols={12}
      compactType="vertical"
      draggableHandle=".drag-handle"
      isDraggable={true}
      isResizable={true}
      layout={layouts}
      preventCollision={false}
      rowHeight={100}
      verticalCompact={true}
      width={1200}
      onLayoutChange={handleLayoutChange}
    >
      {items.map((item) => (
        <div key={item.id} className="bg-white rounded-lg shadow-lg relative overflow-auto">
          <div className="drag-handle absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 right-0 h-6 bg-transparent cursor-move pointer-events-auto" />
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-transparent cursor-move pointer-events-auto" />
            <div className="absolute top-6 bottom-6 left-0 w-6 bg-transparent cursor-move pointer-events-auto" />
            <div className="absolute top-6 bottom-6 right-0 w-6 bg-transparent cursor-move pointer-events-auto" />
          </div>
          <div className="pl-4 pt-4 flex items-center gap-1">
            {item.icon && <item.icon className="h-3.5 w-3.5 text-gray-500" />}
            <h2 className="text-sm font-bold text-gray-700 truncate">
              {item.widget_id ? t(`widgets.${item.widget_id}.title`) : item.title}
            </h2>
          </div>
          <div className="absolute top-2 right-2 z-10 flex gap-1">
            {item.configComponent && !item.isLocked && (
              <button
                className={cn('p-1 rounded-full hover:bg-gray-100 transition-colors', {
                  'bg-gray-100': flippedWidgets.has(item.id),
                })}
                onClick={() => toggleConfig(item.id)}
              >
                <Cog6ToothIcon className="h-5 w-5 text-gray-500" />
              </button>
            )}
            <button
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              onClick={() => onRemoveWidget(item.id)}
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          <ReactCardFlip
            containerClassName={cn(
              'p-4 relative',
              { 'w-full h-full': item.adjustable },
              { 'blur-sm pointer-events-none select-none': item.isLocked },
            )}
            flipDirection="horizontal"
            isFlipped={!item.isLocked && flippedWidgets.has(item.id)}
          >
            <div className="w-full h-full">
              <item.component metadata={item.metadata} widgetId={item.id} />
            </div>

            <div className="w-full h-full">
              {item.configComponent && (
                <item.configComponent
                  metadata={item.metadata}
                  widgetId={item.id}
                  onClose={() => handleConfigClose(item.id)}
                />
              )}
            </div>
          </ReactCardFlip>
          {item.isLocked && onLockedWidgetClick && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/80 z-[5] pointer-events-none">
              <div
                className="flex flex-col items-center justify-center gap-3 cursor-pointer pointer-events-auto"
                onClick={onLockedWidgetClick}
              >
                <SparklesIcon className="h-10 w-10 text-gray-900" />
                <p className="text-sm font-medium text-gray-700">{t('dashboard.upgradeToUnlock')}</p>
                <button
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-all shadow-sm hover:shadow-md active:scale-95"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLockedWidgetClick();
                  }}
                >
                  {t('userMenu.upgradeToPremium')}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </ReactGridLayout>
  );
}
